// @ts-nocheck
import isEmpty from 'lodash/isEmpty';
import toNumber from 'lodash/toNumber';
import isArray from 'lodash/isArray';
import { momentTz } from '../dates';

import { isValueEmpty, isJsonString } from '../core';
import { YYYY_MM_DD_DATE_FORMAT } from '../../const/CommonConst';

/**
 * Safely parse JSON from localStorage
 * @param {string} key - localStorage key
 * @param {*} defaultValue - default value if parsing fails
 * @returns {*} parsed value or default value
 */
export function safeJsonParse(key, defaultValue = null) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : defaultValue;
  } catch (error) {
    console.warn(`Failed to parse ${key} from localStorage:`, error);
    return defaultValue;
  }
}

/**
 * Safely parses a JSON string and falls back when parsing fails.
 * @param {string} value
 * @param {*} defaultValue
 * @returns {*}
 */
export function parseSafeString(value, defaultValue = null) {
  if (typeof value !== 'string') {
    return defaultValue;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Failed to parse JSON string:', error);
    return defaultValue;
  }
}

/**
 * Converts a file to Base64 format.
 * @param {File} file - The file to convert to Base64.
 * @returns {Promise<string>} A promise that resolves to the Base64 representation of the file.
 */
export const convertFileToBase64 = (file, split = true) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = split ? reader.result.split(',')[1] : reader.result;
      resolve(base64Data);
    };
    reader.onerror = (error) => {
      reject(new Error(error));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * @function convertToNormalValue
 * @description Converts a given input to a normal value (either a parsed JSON object, trimmed string, or the original input if not in JSON format).
 *
 * @param {string | null | undefined} input - The input to be converted to a normal value.
 * @returns {any} The converted value. It could be a JSON-parsed object, a trimmed string, or the original input.
 */
export const convertToNormalValue = (input) => {
  try {
    if (input === null || input === undefined) {
      return null; // Return null for empty strings
    }

    if (typeof input === 'string') {
      const trimmedInput = input?.trim();
      if (trimmedInput === '') {
        return null; // Return null for empty strings
      }

      // Attempt to parse JSON
      return JSON.parse(trimmedInput);
    }

    // Return input as is for non-string types
    return input;
  } catch {
    // Return the original input for non-JSON-parsable strings
    return typeof input === 'string' ? input.trim() : input;
  }
};

/**
 * @function renameDuplicates
 * @description Function to rename duplicate columns
 * @param {array}- name of columns
 */
const renameDuplicates = (columns) => {
  const columnCount = {};
  return columns?.map((col) => {
    if (columnCount[col]) {
      columnCount[col] += 1;
      return `${col}_${columnCount[col]}`;
    }
    columnCount[col] = 1;
    return col;
  });
};

/**
 * @function convertData
 * @description Parses a CSV file and returns the parsed data as an array of objects.
 * Handles empty cells properly by padding missing values with empty strings.
 * @param {string} csvData - The CSV data as a string.
 * @param {number} maxRows - Number of max rows conversion allowed.
 * @param {Function} reject - Function for rejecting promise errors.
 * @returns {Array} An array of objects representing the parsed CSV data.
 */
const convertData = (csvData, maxRows, reject) => {
  const splitCSV = (row) => {
    // Improved CSV parsing to handle empty cells properly
    const cells = [];
    let current = '';
    let inQuotes = false;
    let i = 0;

    while (i < row.length) {
      const char = row[i];
      const nextChar = row[i + 1];

      if (char === '"' && !inQuotes) {
        inQuotes = true;
      } else if (char === '"' && inQuotes) {
        if (nextChar === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else if (char === ',' && !inQuotes) {
        // Add the last cell
        cells.push(convertToNormalValue(current.trim()));
        current = '';
      } else {
        current += char;
      }
      i++;
    }

    // Add the last cell
    cells.push(convertToNormalValue(current.trim()));
    return cells;
  };

  const rows = csvData?.split(/\r?\n/).filter(Boolean);

  // Extract and normalize headers
  const formattedHeaders = splitCSV(rows[0]);
  const headers = renameDuplicates(formattedHeaders);
  const jsonData = [];
  if (maxRows < rows?.length) {
    reject({ message: `Max ${maxRows} rows allowed in file.` });
    return [];
  }

  for (let i = 1; i < rows?.length; i++) {
    const values = splitCSV(rows[i]);

    // Ensure we have the same number of values as headers by padding with empty strings
    while (values.length < headers.length) {
      values.push('');
    }

    // Truncate if we have more values than headers (shouldn't happen with proper CSV)
    if (values.length > headers.length) {
      values.length = headers.length;
    }

    const obj = {};
    for (let j = 0; j < headers.length; j++) {
      obj[headers[j]] = convertToNormalValue(values[j] ?? '');
    }
    jsonData.push(obj);
  }
  if (isArray(jsonData)) {
    return jsonData;
  }
  return [];
};

/**
 * @function convertCsvToJson
 * @description Parses a CSV file and returns the parsed data as an array of objects.
 * Handles empty cells properly and ensures all rows have consistent structure.
 * @param {File} file - The CSV file to parse.
 * @param {number} maxRows - Number of max rows conversion allowed (default value is 100).
 * @returns {Promise<Object[]>} A promise that resolves to the parsed data as an array of objects.
 */
export const convertCsvToJson = (file, maxRows = 1001) => {
  return new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const csvData = e?.target?.result;
        const data = convertData(csvData, maxRows, reject);
        resolve(data);
      };

      reader.onerror = (error) => {
        reject(new Error(`FileReader error: ${error.message}`));
      };
      // Read the file as text.
      reader.readAsText(file, 'utf8');
    }
  });
};

/**
 * The function converts a default attribute value to a number if the attribute data type is Integer or
 * Decimal.
 * @returns The function `convertDefaultValueToNumber` returns a number value if the
 * `attributeDataType` is either "Integer" or "Decimal" and the `attributeDefaultValue` is a valid
 * number. If the `attributeDefaultValue` is not a valid number, an empty string is returned. If the
 * `attributeDataType` is not "Integer" or "Decimal", the `attributeDefaultValue` is returned as is
 */
export const convertDefaultValueToNumber = (
  attributeDataType,
  attributeDefaultValue
) => {
  if (attributeDataType === 'Integer' || attributeDataType === 'Decimal') {
    if (!isValueEmpty(attributeDefaultValue)) {
      // If it's already a number, just return it
      if (typeof attributeDefaultValue === 'number') {
        return !Number.isNaN(attributeDefaultValue)
          ? attributeDefaultValue
          : '';
      }

      if (
        typeof attributeDefaultValue === 'string' &&
        attributeDefaultValue.trim() !== ''
      ) {
        const numberValue = toNumber(attributeDefaultValue);
        if (!Number.isNaN(numberValue)) {
          return numberValue;
        }
      }
    }
    return '';
  }
  if (attributeDataType === 'Association') {
    return null; // Return null for empty strings
  }

  return attributeDefaultValue;
};

/**
 * Converts comma separated string to array, removes preceding and trailing spaces
 * @param {string} value - comma separated string to be converted to array
 * @returns {Array} Array of strings by removing all preceding and trailing spaces
 */
export const toMultiselectArray = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) return value.map((item) => String(item).trim());
  if (typeof value === 'string')
    return value.split(',').map((item) => item.trim());
  return [String(value).trim()];
};

/**
 * @description Format the default value based on data type.
 * @param {object} values - Object containing data type and default value.
 * @returns {string} - Formatted default value.
 */
export const formatDefaultValue = (values) => {
  if (values?.data_type === 'Date' && !isEmpty(values?.default_value)) {
    return momentTz(values?.default_value).format(YYYY_MM_DD_DATE_FORMAT);
  }
  if (values?.data_type === 'Character' && !isEmpty(values?.default_value)) {
    if (typeof values?.default_value === 'string') {
      return values?.default_value?.trim();
    }

    if (Array.isArray(values?.default_value)) {
      return values?.default_value
        .map((item) => {
          if (typeof item === 'string') {
            return item?.trim();
          }

          if (item && typeof item === 'object') {
            return String(item?.value ?? item?.label ?? '').trim();
          }

          return String(item ?? '').trim();
        })
        .filter((item) => item?.length > 0);
    }

    if (values?.default_value && typeof values?.default_value === 'object') {
      return String(
        values?.default_value?.value ?? values?.default_value?.label ?? ''
      ).trim();
    }

    return values?.default_value;
  }
  return values?.default_value;
};

/**
 * Converts a value into a string representation suitable for use in URL query parameters.
 *
 * @param {*} s - The value to be serialized.
 *   - If `s` is a string, it is URL-encoded.
 *   - If `s` is a number, it is converted to a string.
 *   - If `s` is an object or any other type, it is converted to a JSON string.
 *
 * @returns {string} - The serialized string representation of the input value.
 *
 * @example
 * customSerializeForNumberStringTabValue('hello world'); // "hello%20world"
 * customSerializeForNumberStringTabValue(123); // "123"
 * customSerializeForNumberStringTabValue({ key: 'value' }); // '{"key":"value"}'
 */
export const customSerializeForNumberStringTabValue = (s) => {
  if (typeof s === 'string') {
    return s;
  }
  if (typeof s === 'number') {
    return s.toString();
  }
  return JSON.stringify(s);
};

/**
 * Converts a string representation back into its original value.
 *
 * @param {string} s - The string value to be deserialized.
 *   - If `s` can be parsed as JSON, it is parsed into its original type.
 *   - If the parsed value is a string, it is URL-decoded.
 *   - If the parsed value is a number, it is converted to a number type.
 *   - If parsing fails, the original string is returned.
 *
 * @returns {*} - The deserialized value in its original type.
 *
 * @example
 * customDeserializeForNumberStringTabValue('hello%20world'); // "hello world"
 * customDeserializeForNumberStringTabValue('123'); // 123
 * customDeserializeForNumberStringTabValue('{"key":"value"}'); // { key: "value" }
 */
export const customDeserializeForNumberStringTabValue = (s) => {
  try {
    const parsed = JSON.parse(s);
    if (typeof parsed === 'string') {
      return decodeURIComponent(parsed);
    }
    if (typeof parsed === 'number') {
      return Number(parsed);
    }
    return parsed;
  } catch {
    return s;
  }
};

/**
 * Normalizes a payload object by converting empty string values to null.
 * @param {Object} body - The payload object to normalize.
 * @returns {Object|null} The normalized object or null if input is falsy.
 */
export const normalizePayload = (body) => {
  return body
    ? Object.fromEntries(
        Object.entries(body).map(([key, val]) => [key, val === '' ? null : val])
      )
    : null;
};

/** Recursively extracts plain text from a TipTap JSON node tree. */
const extractTextFromTipTapNode = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return '\n';
  if (!Array.isArray(node.content)) return '';

  const isBlockContainer = [
    'doc',
    'listItem',
    'bulletList',
    'orderedList',
    'blockquote',
  ].includes(node.type);
  const separator = isBlockContainer ? '\n' : '';

  return node.content.map(extractTextFromTipTapNode).join(separator);
};

/**
 * Extracts plain text from rich-text JSON (DraftJS or TipTap) or returns the string as-is.
 * Handles:
 *  - TipTap JSON (node tree with `type: 'doc'`)
 *  - Legacy DraftJS JSON (`blocks[].text`)
 *  - Plain text strings
 * @param {string} value - JSON string or plain text string
 * @returns {string} Plain text content
 */
export const getPlainTextFromEditorState = (value) => {
  if (isValueEmpty(value)) {
    return '';
  }
  if (isJsonString(value)) {
    try {
      const parsed = JSON.parse(value);
      // TipTap JSON format
      if (parsed?.type === 'doc') {
        return extractTextFromTipTapNode(parsed).trim();
      }
      // Legacy DraftJS JSON format
      if (Array.isArray(parsed?.blocks)) {
        return parsed.blocks
          .map((b) => b.text || '')
          .join('\n')
          .trim();
      }
      return '';
    } catch (error) {
      console.warn('Failed to parse editor state JSON:', error);
      return '';
    }
  }
  return String(value).trim();
};

// Helper function to convert column index to Excel-style column name (A, B, ..., Z, AA, AB, etc.)
export const getColumnName = (columnIndex) => {
  let columnName = '';
  let index = columnIndex;

  while (index > 0) {
    index--; // Convert to 0-based for calculation
    columnName = String.fromCharCode(65 + (index % 26)) + columnName;
    index = Math.floor(index / 26);
  }

  return columnName;
};

// Parse cell ID to get column and row indices
export const parseCellId = (cellId) => {
  if (!cellId) return [0, 0];

  // Input validation to prevent ReDoS attacks
  if (typeof cellId !== 'string' || cellId.length > 20) {
    return [0, 0];
  }

  // More specific regex to prevent backtracking - matches Excel-style cell IDs
  const regex = /^([A-Z]{1,3})(\d{1,7})$/;
  const match = regex.exec(cellId);
  if (!match) return [0, 0];

  const colString = match[1];
  const rowNum = parseInt(match[2], 10);

  let colNum = 0;
  for (let i = 0; i < colString.length; i += 1) {
    colNum = colNum * 26 + (colString.charCodeAt(i) - 64);
  }

  return [colNum, rowNum];
};
